import { Checkbox, Chip } from '@heroui/react';
import { useState } from 'react';
import { BiFlag, BiShow, BiTrash } from 'react-icons/bi';
import { FiCalendar, FiExternalLink, FiMaximize2, FiUser } from 'react-icons/fi';
import { MdOutlineHideSource } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { Confirm, cn, Dialog } from '@/core/ui';
import type { QuestionComment } from '@/graphql/gql/graphql';
import { useGlobal } from '@/store/global';
import { getDbTypeHashFragment } from '@/utils';
import { formatDateFromUnix } from '@/utils/date';

type ConfirmAction = 'delete' | 'hide' | 'unhide' | 'review' | 'unreview' | null;

const ACTION_META: Record<
  NonNullable<ConfirmAction>,
  { title: string; description: string; type: 'danger' | 'warn' | 'default' }
> = {
  delete: {
    title: 'Delete Comment',
    description: 'Are you sure you want to delete this comment?',
    type: 'danger',
  },
  hide: { title: 'Hide Comment', description: 'Are you sure you want to hide this comment?', type: 'warn' },
  unhide: {
    title: 'Unhide Comment',
    description: 'Are you sure you want to unhide this comment?',
    type: 'warn',
  },
  review: {
    title: 'Review Comment',
    description: 'Are you sure you want to add this comment to review?',
    type: 'default',
  },
  unreview: {
    title: 'Unreview Comment',
    description: 'Are you sure you want to remove this comment from review?',
    type: 'default',
  },
};

const CommentCard = ({
  comment,
  serialNo,
  isSelected,
  onSelect,
  onAction,
}: {
  comment: QuestionComment;
  serialNo: number;
  isSelected: boolean;
  onSelect: (id: string | number, selected: boolean) => void;
  onAction: (ids: (string | number)[], operation: string) => void;
}) => {
  const [pendingAction, setPendingAction] = useState<ConfirmAction>(null);
  const dbType = useGlobal.use.dbType();

  const isHidden = comment.review === true;
  const isReply = !!comment.parentId;
  const isInReview = !!comment.recordReviewId;
  const openConfirm = (action: NonNullable<ConfirmAction>) => setPendingAction(action);

  const handleConfirm = () => {
    if (pendingAction) {
      onAction([String(comment.id)], pendingAction);
      setPendingAction(null);
    }
  };

  const meta = pendingAction ? ACTION_META[pendingAction] : null;

  return (
    <>
      <div
        className={cn(
          'bg-content1 rounded-xl border shadow-sm flex flex-col gap-2 px-3 pt-3 pb-1 h-full transition-colors',
          isSelected ? 'border-primary-300 bg-primary-50/30' : 'border-default-200',
        )}
      >
        {/* Header: sr#, id chip, checkbox */}
        <div className='flex items-center justify-between gap-2'>
          <div className='flex items-center gap-1.5 min-w-0'>
            <span className='text-xs font-mono text-default-400 shrink-0'>#{serialNo}</span>
            <Chip size='sm' variant='flat' color='default' className='text-xs font-mono shrink-0'>
              {comment.id}
            </Chip>
          </div>
          <Checkbox
            size='sm'
            isSelected={isSelected}
            onValueChange={(checked) => onSelect(comment.id as string | number, checked)}
            aria-label='Select comment'
          />
        </div>

        {/* Comment text — always 2 lines, show more → modal */}
        <div
          className={cn('rounded-lg p-2', {
            'bg-content2 border border-default-200': !isHidden,
            'bg-warning-50 border border-warning-200': isHidden,
          })}
        >
          <code className='text-sm text-default-800 wrap-break-word whitespace-pre-wrap line-clamp-8'>
            {comment.comment || '-'}
          </code>
        </div>

        {/* User */}
        <div className='flex items-center gap-1.5'>
          <FiUser className='size-3 text-default-400 shrink-0' />
          <span className='text-xs text-default-700 font-medium truncate'>
            {comment.user?.displayName ||
              (comment.user?.username ? `@${comment.user.username}` : 'Anonymous')}
          </span>
        </div>

        {/* QID + date */}
        <div className='flex items-center justify-between gap-2'>
          {comment.questionId ? (
            <Link
              target='_blank'
              rel='noopener noreferrer'
              to={
                ROUTES.CONTENT.EDIT.QUESTION.replace(':id', String(comment.questionId)) +
                getDbTypeHashFragment('global-state', { dbType })
              }
              className='flex items-center gap-1 text-xs text-default-500 hover:text-primary-500'
            >
              <FiExternalLink className='size-3 shrink-0' />
              <span className='font-mono'>Q: {comment.questionId}</span>
            </Link>
          ) : (
            <span />
          )}
          {comment.createdAt && (
            <div className='flex items-center gap-1'>
              <FiCalendar className='size-3 text-default-400 shrink-0' />
              <span className='text-xs text-default-400 whitespace-nowrap'>
                {formatDateFromUnix(comment.createdAt, 'DD MMM YY | HH:mm')}
              </span>
            </div>
          )}
        </div>

        {/* Footer: actions | chips */}
        <div className='flex items-center justify-between border-t border-default-100 mt-auto py-1'>
          <div className='flex items-center gap-0.5'>
            <button
              type='button'
              title='Delete'
              onClick={() => openConfirm('delete')}
              className='p-1 rounded-md hover:bg-danger-50 text-danger-400 hover:text-danger-600 transition-colors'
            >
              <BiTrash className='size-3.5' />
            </button>
            <button
              type='button'
              title={isHidden ? 'Unhide' : 'Hide'}
              onClick={() => openConfirm(isHidden ? 'unhide' : 'hide')}
              className='p-1 rounded-md hover:bg-warning-50 text-warning-500 hover:text-warning-700 transition-colors'
            >
              {isHidden ? <BiShow className='size-3.5' /> : <MdOutlineHideSource className='size-3.5' />}
            </button>
            <button
              type='button'
              title={isInReview ? 'Unreview' : 'Review'}
              onClick={() => openConfirm(isInReview ? 'unreview' : 'review')}
              className='p-1 rounded-md hover:bg-default-100 text-default-500 hover:text-default-700 transition-colors'
            >
              <BiFlag className='size-3.5' />
            </button>
            <Dialog
              title='Comment'
              trigger={(onOpen) => (
                <button
                  type='button'
                  title='View full comment'
                  onClick={onOpen}
                  className='p-1 rounded-md hover:bg-primary-100 text-default-600 hover:text-primary-700 transition-colors'
                >
                  <FiMaximize2 className='size-3.5' />
                </button>
              )}
            >
              <div className='overflow-y-auto max-h-[60vh]'>
                <code className='text-sm text-default-800 wrap-break-word whitespace-pre-wrap'>
                  {comment.comment}
                </code>
              </div>
            </Dialog>
          </div>

          <div className='flex items-center gap-1.5'>
            <div className='w-px h-4 bg-default-200 shrink-0' />
            <Chip size='sm' variant='flat' color={isReply ? 'secondary' : 'default'} className='text-xs'>
              {isReply ? 'Reply' : 'Comment'}
            </Chip>
            <Chip size='sm' variant='flat' color={isHidden ? 'warning' : 'default'} className='text-xs'>
              {isHidden ? 'NO' : 'YES'}
            </Chip>
          </div>
        </div>
      </div>

      {meta && (
        <Confirm
          type={meta.type}
          title={meta.title}
          isOpen={!!pendingAction}
          onOpenChange={(open) => {
            if (!open) setPendingAction(null);
          }}
          message={meta.description}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};

export const CommentsGrid = ({
  rows,
  selectedComments,
  onSelect,
  onAction,
}: {
  rows: Array<{ comment: QuestionComment; serialNo: number }>;
  selectedComments: Set<string | number>;
  onSelect: (id: string | number, selected: boolean) => void;
  onAction: (ids: (string | number)[], operation: string) => void;
}) => {
  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 p-4 overflow-auto max-h-[calc(100vh-400px)] scrollbar-thin'>
      {rows.map(({ comment, serialNo }) => (
        <CommentCard
          key={comment.id}
          comment={comment}
          serialNo={serialNo}
          isSelected={selectedComments.has(comment.id as string | number)}
          onSelect={onSelect}
          onAction={onAction}
        />
      ))}
    </div>
  );
};
